import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  AlertTriangle,
  Lock,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile à Fontenay-le-Fleury ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons à domicile {city.aCity} : trois
            engagements pour votre sérénité
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la préparation de vos cartons {city.aCity} requiert
            toute votre attention, notre service de livraison
            s&apos;invite chez vous en s&apos;appuyant sur trois piliers
            fondamentaux.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Ponctualité absolue",
              body: "Nous convenons ensemble d'un créneau horaire précis pour que vous n'ayez pas à attendre toute la journée, et nous livrons également les villes voisines comme Saint-Cyr-l'École, Bois-d'Arcy, Villepreux et Noisy-le-Roi.",
              Icon: Calendar,
            },
            {
              title: "Garantie de propreté",
              body: "Vos fournitures vous sont remises à plat, à l'abri des intempéries, parfaitement sèches, saines et prêtes à être pliées.",
              Icon: ShieldCheck,
            },
            {
              title: "Flexibilité des volumes",
              body: "Du simple kit étudiant pour un studio aux volumes massifs pour les pavillons familiaux, nous ajustons notre stock pour répondre instantanément à l'urgence de votre demande.",
              Icon: Boxes,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-8 max-w-3xl text-[15px] md:text-base text-[#3a4856] leading-relaxed">
          Ce triptyque logistique vous libère des contraintes techniques
          les plus lourdes. En évitant les trajets chronophages vers les
          grandes surfaces de bricolage, vous économisez votre énergie
          pour vous concentrer sur l&apos;essentiel : le tri de vos effets
          personnels et l&apos;organisation de votre départ.
        </p>
      </div>
    </section>

    {/* ======= SECTION 3 — Rouleaux adhésifs professionnels ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Adhésifs professionnels
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Le secret d&apos;un scellage inviolable
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              On sous-estime trop souvent l&apos;importance du ruban
              adhésif, pourtant garant de la structure même de vos colis.
              Notre boutique en ligne propose exclusivement des rouleaux
              de qualité industrielle dotés d&apos;une masse adhésive à
              forte adhérence, capables de résister aux variations de
              température et à l&apos;humidité des espaces de stockage.
              Contrairement aux produits standards du commerce qui se
              décollent sous la tension, notre scotch technique fusionne
              littéralement avec le carton ondulé pour maintenir les
              rabats sous une pression constante, même lorsque le
              contenant est chargé au maximum de sa capacité.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Cette robustesse vous met définitivement à l&apos;abri des
              ouvertures accidentelles lors des phases critiques de
              levage et de portage. En équipant vos cartons de cette
              fermeture haute sécurité, vous stabilisez vos piles dans le
              camion de déménagement et vous vous assurez que la
              poussière ou les frottements n&apos;altéreront en rien le
              contenu de vos boîtes pendant le voyage vers
              {" "}{city.name}.
            </p>

            <Link
              to="/fermeture-accessoires"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-fontenay-le-fleury-cta-adhesifs"
            >
              <Tag className="h-4 w-4" strokeWidth={2.6} />
              Voir nos rouleaux adhésifs
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Lock className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Fermeture haute sécurité
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Un adhésif industriel qui fusionne avec le carton ondulé,
              résiste aux variations de température et tient sous
              pression maximale.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Masse adhésive forte adhérence
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Résiste à l&apos;humidité &amp; au froid
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Stabilise les piles dans le camion
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Anti-ouverture accidentelle
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Erreurs à éviter ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Erreurs à éviter
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les erreurs à éviter lors de l&apos;emballage de vos affaires
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous garantir un sans-faute le jour J, voici les cinq
            pièges les plus fréquents à contourner absolument lors de vos
            préparatifs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "Erreur 1",
              title: "Surcharger les grands volumes",
              body: "Ne remplissez jamais un grand carton uniquement avec des livres ; réservez les charges lourdes aux petits formats pour éviter les déchirures.",
            },
            {
              n: "Erreur 2",
              title: "Laisser des espaces vides",
              body: "Un carton mal comblé s'écrase sous le poids des autres ; comblez le vide avec du papier bulle ou des tissus.",
            },
            {
              n: "Erreur 3",
              title: "Croiser les rabats sans adhésif",
              body: "Plier les rabats les uns sur les autres sans mettre de scotch fragilise la structure et provoque l'effondrement du fond.",
            },
            {
              n: "Erreur 4",
              title: "Oublier d'étiqueter les tranches",
              body: "Écrire uniquement sur le dessus rend l'étiquette invisible une fois les cartons empilés dans le camion.",
            },
            {
              n: "Erreur 5",
              title: "Emballer la vaisselle à plat",
              body: "Les assiettes doivent être positionnées à la verticale (sur la tranche) dans les cartons pour mieux absorber les chocs de la route.",
            },
          ].map(({ n, title, body }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  {n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 max-w-3xl text-[15px] md:text-base text-[#3a4856] leading-relaxed">
          L&apos;évitement de ces erreurs courantes change radicalement
          l&apos;expérience de votre déménagement. En adoptant ces
          réflexes simples mais professionnels, vous gagnez un temps
          précieux lors du déballage dans votre nouvelle demeure
          {" "}{city.aCity} et vous vous offrez la certitude que chaque
          objet, de la verrerie fine aux archives familiales, ressortira
          de sa boîte dans un état absolument impeccable.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-fontenay-le-fleury-cta-protection"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir les emballages de protection
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-fontenay-le-fleury-cta-shop"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-fontenay-le-fleury-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
